/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as posedetection from "@tensorflow-models/pose-detection";
import * as scatter from "scatter-gl";

import * as params from "./params";
import { config } from "../vision-algo/env_config";

// These anchor points allow the pose pointcloud to resize according to its
// position in the input.
const ANCHOR_POINTS = [
  [0, 0, 0],
  [0, 1, 0],
  [-1, 0, 0],
  [-1, -1, 0],
];

// #ffffff - White
// #800000 - Maroon
// #469990 - Malachite
// #e6194b - Crimson
// #42d4f4 - Picton Blue
// #fabed4 - Cupid
// #aaffc3 - Mint Green
// #9a6324 - Kumera
// #000075 - Navy Blue
// #f58231 - Jaffa
// #4363d8 - Royal Blue
// #ffd8b1 - Caramel
// #dcbeff - Mauve
// #808000 - Olive
// #ffe119 - Candlelight
// #911eb4 - Seance
// #bfef45 - Inchworm
// #f032e6 - Razzle Dazzle Rose
// #3cb44b - Chateau Green
// #a9a9a9 - Silver Chalice
const COLOR_PALETTE = [
  "#ffffff",
  "#800000",
  "#469990",
  "#e6194b",
  "#42d4f4",
  "#fabed4",
  "#aaffc3",
  "#9a6324",
  "#000075",
  "#f58231",
  "#4363d8",
  "#ffd8b1",
  "#dcbeff",
  "#808000",
  "#ffe119",
  "#911eb4",
  "#bfef45",
  "#f032e6",
  "#3cb44b",
  "#a9a9a9",
];
let videostream;
const cv = window.cv;
//VIDEO_MODE=true means analysis will work on Preloaded Video
//VIDEO_MODE=false Means analysis will work on real time camera
const VIDEO_MODE = !config.use_live_camera;

const screen = window.screen;

class videoStreamGenerator {
  constructor() {
    const selectedPatient = localStorage.getItem("patientName");
    const selectedAssesment = sessionStorage.getItem("selectedAssesment");

    this.video = document.getElementById("video");
    this.videoLinks = {
      22: {
        "NAYAB345 345": "videos/patient-saad-gait.MOV",
        "Saad_002 Saad_002": "videos/patient-saad-gait.MOV",
        "Yaseen Pat_01": "videos/patient-saad-gait.MOV",
      },
      35: {
        "Saad_002 Saad_002": "videos/sitstand-saad.MOV",
        "Yaseen Pat_01": "videos/sitstand-saad.MOV",
        "NAYAB345 345": "videos/sitstand-saad.MOV",
        // 2: "videos/video2.mp4",
        // 3: "videos/video3.mp4",
      },
      55: {
        "NAYAB345 345": "videos/Raw_FR_PS5.MOV",
        "Yaseen Pat_01": "videos/Raw_FR_PS6.MOV",
        "Saad_002 Saad_002": "videos/Raw_FR_PS4.MOV",
      },
      1: {
        // "NAYAB345 345": "videos/mCTSIB1.MOV",
        "NAYAB345 345": "videos/mCTSIBTest1.MOV",
        "Yaseen Pat_01": "videos/mCTSIBTest2.MOV",
        "Saad_002 Saad_002": "videos/mCTSIBTest4.MOV",
      },
      56: {
        "NAYAB345 345": "videos/Raw_FR_PS5.MOV",
        "Yaseen Pat_01": "videos/Raw_FR_PS6.MOV",
        "Saad_002 Saad_002": "videos/Raw_FR_PS4.MOV",
      },
      31: {
        "NAYAB345 345": "videos/tug1.MOV",
        "Saad_002 Saad_002": "videos/tug_demo.MOV",
      },
    };

    const videoLink = this.videoLinks[selectedAssesment][selectedPatient];
    this.video.src = videoLink;
    // this.video.src = "videos/new-gait-calibration-chk.MOV";
    videostream = null;
  }
  async CreateStream() {
    try {
      if (videostream) {
        return;
      }
      const vs = new videoStreamGenerator();
      if (this.video.captureStream) {
        this.videostream = this.video.captureStream();
      } else if (this.video.mozCaptureStream) {
        videostream = this.video.mozCaptureStream();
      } else {
        console.log("captureStream() not supported");
      }
    } catch (exp) {
      console.log(exp);
    }
  }
}

export class Camera {
  constructor() {
    if (VIDEO_MODE) {
      //check to determine weather to run analysis on video or camera
      const vs = new videoStreamGenerator();
      if (vs.video.captureStream) {
        this.videostream = vs.video.captureStream();
      }
      this.video = vs.video;
      this.stream = vs.videostream;
    } else {
      this.video = document.getElementById("video");
    }
    this.canvas = document.getElementById("output");
    this.ctx = this.canvas.getContext("2d");
    this.scatterGLEl = document.querySelector("#scatter-gl-container");
    this.scatterGL = new scatter.ScatterGL(this.scatterGLEl, {
      rotateOnStart: true,
      selectEnabled: false,
      styles: { polyline: { defaultOpacity: 1, deselectedOpacity: 1 } },
    });
    this.scatterGLHasInitialized = false;
    this.videoCapture = null;
    this.crop_params = {};

    this.canvas_width = screen.width;
    this.canvas_height = screen.height;
  }

  /**
   * Initiate a Camera instance and wait for the camera stream to be ready.
   * @param cameraParam From app `STATE.camera`.
   */
  static async setupCamera(cameraParam) {
    const start_time = performance.now();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error(
        "Browser API navigator.mediaDevices.getUserMedia not available"
      );
    }

    console.log(navigator.mediaDevices.getSupportedConstraints());
    console.log(
      performance.now() - start_time,
      "ms to getSupportedConstraints"
    );
    const { targetFPS, sizeOption } = cameraParam;
    const $size = params.VIDEO_SIZE[sizeOption];

    const videoConfig = {
      audio: true, // we are not using audio from here, but still need to set it true to avoid camera freeze when audio listerner is turned on
      video: {
        facingMode: "user",
        focusMode: { exact: ["manual"] }, // Setting AutoFocus Off for mobiles
        aspectRatio: 16 / 9,
        width: { ideal: $size.width },
        height: { ideal: $size.height },
        frameRate: {
          ideal: targetFPS,
        },
      },
    };
    let tempStream = "";
    if (VIDEO_MODE) {
      //check to determine weather to run analysis on video or
      const vs = new videoStreamGenerator();
      vs.CreateStream(); //Getting Video Stream
      this.needsDepthBuffer = true;
    } else {
      //For Getting Camera Feed
      try {
        tempStream = await navigator.mediaDevices.getUserMedia(videoConfig);
        // turning on the audio above is causing volume ducking issues when an instruction audio is played
        // so we are using a dummy audio track and disabling it right away
        tempStream.getAudioTracks().forEach((track) => {
          track.enabled = false;
          track.stop();
          tempStream.removeTrack(track);
        });
        console.log(performance.now() - start_time, "ms to getUserMedia");
      } catch (error) {
        console.error("Error accessing media devices:", error);
      }
    }
    const stream = tempStream;
    const camera = new Camera();
    let tempWidth, tempHeight;

    let dvTopHeader = document.querySelector(".top-header-wrap");
    if (VIDEO_MODE) {
      //check to determine weather to run analysis on video or
      tempWidth = $size.width;
      tempHeight = $size.height;
    } else {
      camera.video.srcObject = stream;
      await new Promise((resolve) => {
        camera.video.onloadedmetadata = (video) => {
          resolve(video);
        };
      });
      camera.video.play().catch((error) => {
        console.error("Race condition error:", error);
      });
      tempWidth = camera.video.videoWidth;
      tempHeight = camera.video.videoHeight;
    }

    const videoWidth = tempWidth;
    const videoHeight = tempHeight;

    // Must set below two lines, otherwise video element doesn't show.
    camera.video.width = videoWidth;
    camera.video.height = videoHeight;

    camera.canvas.width = screen.width;
    camera.canvas.height = screen.height - dvTopHeader.offsetHeight;
    camera.videoCapture = new cv.VideoCapture(camera.video);

    if (videoWidth > videoHeight) {
      camera.crop_params.x = (videoWidth - videoHeight) / 2;
      camera.crop_params.width = videoWidth;
      camera.crop_params.y = 0;
      camera.crop_params.height = videoHeight;
    } else {
      camera.crop_params.x = 0;
      camera.crop_params.width = videoWidth;
      camera.crop_params.y = 0;
      camera.crop_params.height = videoHeight;
    }

    camera.canvas_width = screen.width;
    camera.canvas_height = screen.height - dvTopHeader.offsetHeight;

    const canvasContainer = document.querySelector(".canvas-wrapper");
    if (VIDEO_MODE) {
      //check to determine weather to run analysis on video or
      canvasContainer.style = `width: ${videoWidth}px; height: ${videoHeight}px`;
    } else {
      canvasContainer.style = `width: ${videoWidth}px; height: ${videoHeight}px`;
    }

    camera.scatterGLEl.style = `width: ${videoWidth}px; height: ${videoHeight}px;`;
    camera.scatterGL.resize();

    camera.scatterGLEl.style.display = params.STATE.modelConfig.render3D
      ? "inline-block"
      : "none";
    if (VIDEO_MODE) {
      //check to determine weather to run analysis on video or
      camera.getVideoDimensions();
    }

    return camera;
  }

  flip_horizontal() {
    this.ctx.translate(this.canvas_width, 0);
    this.ctx.scale(-1, 1);
  }

  drawCtx() {
    this.ctx.drawImage(
      this.video,
      0,
      0,
      this.video.videoWidth,
      this.video.videoHeight,
      0,
      0,
      this.canvas_width,
      this.canvas_height
    );
  }
  //For getting running Video height and width
  getVideoDimensions() {
    return new Promise((resolve) => {
      const camera = new Camera();
      // place a listener on it
      camera.video.addEventListener(
        "loadedmetadata",
        function () {
          // retrieve dimensions
          const height = this.videoHeight;
          const width = this.videoWidth;
          // camera.Height=this.videoHeight;
          //camera.Width=this.videoWidth;
          //console.log('VideoHeight: ',this.videoHeight);
          //console.log('VideoHeight: ',this.videoWidth);

          // send back result
          resolve({ height, width });
        },
        false
      );
    });
  }
  clearCtx() {
    this.ctx.clearRect(0, 0, this.canvas_width, this.canvas_height);
  }

  scale_to_fit_the_point(point_x, point_y) {
    return this.scale_to_fit(new cv.Point(point_x, point_y));
  }

  draw_line(line, strokeStyle) {
    let start = this.scale_to_fit(new cv.Point(line.x1, line.y1));
    let stop = this.scale_to_fit(new cv.Point(line.x2, line.y2));
    this.ctx.beginPath();
    var c = document.getElementById("output");
    this.ctx.moveTo(Math.floor(c.width / 2), Math.floor(start.y));
    this.ctx.lineTo(Math.floor(c.width / 2) + 200, Math.floor(stop.y));
    // this.ctx.moveTo(start.x, start.y);
    // this.ctx.lineTo(stop.x, stop.y);
    this.ctx.lineWidth = 5;
    this.ctx.strokeStyle = strokeStyle; // '#000000';
    this.ctx.stroke();
  }

  /**
   * Draw the keypoints and skeleton on the video.
   * @param poses A list of poses to render.
   */
  drawResults(poses, current_model = posedetection.SupportedModels.MoveNet, patientId = null) {
    for (const pose of poses) {
      this.drawResult(pose, current_model, patientId);
    }
  }

  /**
   * Draw the keypoints and skeleton on the video.
   * @param pose A pose with keypoints to render.
   */
  drawResult(pose, current_model, patientId = null) {
    if (pose.keypoints != null) {
      this.drawKeypoints(pose.keypoints, current_model);
      // For BlazePose (single-pose), patientId will be 0 and pose.id will be undefined
      // In this case, treat the pose as patient if patientId is 0
      const isPatient = (patientId !== null && (pose.id === patientId || (patientId === 0 && pose.id === undefined)));
      this.drawSkeleton(pose.keypoints, pose.id, current_model, isPatient);
      // this.drawBox(pose);
    }
    if (pose.keypoints3D != null && params.STATE.modelConfig.render3D) {
      this.drawKeypoints3D(pose.keypoints3D);
    }
  }

  /**
   * Draw the keypoints on the video.
   * @param keypoints A list of keypoints.
   */
  drawKeypoints(keypoints, current_model) {
    const keypointInd =
      posedetection.util.getKeypointIndexBySide(current_model);

    // Define face keypoint indices to exclude
    // MoveNet: 0-4 (nose, left_eye, right_eye, left_ear, right_ear)
    // BlazePose: 0-10 (face landmarks)
    const faceKeypointIndices = current_model === posedetection.SupportedModels.BlazePose
      ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      : [0, 1, 2, 3, 4];

    // Set all keypoints to white color
    this.ctx.fillStyle = "White";
    this.ctx.strokeStyle = "White";
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    // Draw middle keypoints (excluding face)
    for (const i of keypointInd.middle) {
      if (!faceKeypointIndices.includes(i)) {
        this.drawKeypoint(keypoints[i]);
      }
    }

    // Draw left keypoints (excluding face)
    for (const i of keypointInd.left) {
      if (!faceKeypointIndices.includes(i)) {
        this.drawKeypoint(keypoints[i]);
      }
    }

    // Draw right keypoints (excluding face)
    for (const i of keypointInd.right) {
      if (!faceKeypointIndices.includes(i)) {
        this.drawKeypoint(keypoints[i]);
      }
    }
  }

  scale_to_fit(keypoint) {
    let hscale = this.canvas_width / this.video.videoWidth;
    let vscale = this.canvas_height / this.video.videoHeight;
    return { x: keypoint.x * hscale, y: keypoint.y * vscale };
  }

  drawKeypoint(keypoint) {
    // If score is null, just show the keypoint.
    const score = keypoint.score ?? 1;
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;
    const kp = this.scale_to_fit(keypoint);
    if (score >= scoreThreshold) {
      const circle = new Path2D();
      circle.arc(kp.x, kp.y, params.DEFAULT_RADIUS, 0, 2 * Math.PI);
      this.ctx.fill(circle);
      this.ctx.stroke(circle);
    }
  }

  drawCircle(center, radius, color) {
    this.ctx.fillStyle = color;
    this.ctx.strokeStyle = "White";
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;
    const circle = new Path2D();
    circle.arc(center.x, center.y, radius, 0, 2 * Math.PI);
    this.ctx.fill(circle);
    this.ctx.stroke(circle);
  }

  /**
   * Draw the skeleton of a body on the video.
   * @param keypoints A list of keypoints.
   */
  drawSkeleton(keypoints, poseId, current_model, isPatient = false) {
    // Use green for patient, otherwise use original color logic
    let color;
    if (isPatient) {
      color = "#00FF00";  // Green for patient
    } else {
      // Original color logic for non-patients
      color = params.STATE.modelConfig.enableTracking && poseId != null
        ? COLOR_PALETTE[poseId % 20]
        : "White";
    }

    this.ctx.fillStyle = color;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    // Define face keypoint indices to exclude from skeleton lines
    // MoveNet: 0-4 (nose, left_eye, right_eye, left_ear, right_ear)
    // BlazePose: 0-10 (face landmarks)
    const faceKeypointIndices = current_model === posedetection.SupportedModels.BlazePose
      ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      : [0, 1, 2, 3, 4];

    posedetection.util.getAdjacentPairs(current_model).forEach(([i, j]) => {
      // Skip drawing skeleton lines that involve face keypoints
      if (faceKeypointIndices.includes(i) || faceKeypointIndices.includes(j)) {
        return;
      }

      const kp1 = keypoints[i];
      const kp2 = keypoints[j];

      // If score is null, just show the keypoint.
      const score1 = kp1.score ?? 1;
      const score2 = kp2.score ?? 1;
      const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;

      if (score1 >= scoreThreshold && score2 >= scoreThreshold) {
        const p1 = this.scale_to_fit(kp1);
        const p2 = this.scale_to_fit(kp2);
        this.ctx.beginPath();
        this.ctx.moveTo(p1.x, p1.y);
        this.ctx.lineTo(p2.x, p2.y);
        this.ctx.stroke();
      }
    });
  }

  drawBox(pose) {
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;
    if (pose.score > scoreThreshold) {
      this.ctx.beginPath();
      let ymin = pose.box.yMin * this.video.videoHeight; // this.crop_params.height + this.crop_params.y;
      let ymax = pose.box.yMax * this.video.videoHeight; //this.crop_params.height + this.crop_params.y;
      let xmin = pose.box.xMin * this.video.videoWidth; //this.crop_params.width + this.crop_params.x;
      let xmax = pose.box.xMax * this.video.videoWidth; //this.crop_params.width + this.crop_params.x;

      let tl = this.scale_to_fit({ x: xmin, y: ymin });
      let br = this.scale_to_fit({ x: xmax, y: ymax });

      this.ctx.rect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
      this.ctx.lineWidth = 2;
      this.ctx.strokeStyle = "red";
      this.ctx.stroke();
    }
  }

  /**
   * The box around the patient, in canvas coordinates (remote patient selection).
   * Built from the confident keypoints, so it also works for poses detected on a cropped frame.
   * @param pose The patient's pose.
   * @returns {{x, y, width, height}|null} Null if too few keypoints are confident.
   */
  patientHighlightRect(pose) {
    const points = pose.keypoints
      .filter((kp) => kp && kp.score >= 0.25)
      .map((kp) => this.scale_to_fit(kp));
    if (points.length < 2) return null;

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const side_padding = 25;
    // Keypoints stop at the eyes and ankles, so leave room for the top of the head and the feet
    const top_padding = 60;
    const bottom_padding = 25;
    return {
      x: Math.min(...xs) - side_padding,
      y: Math.min(...ys) - top_padding,
      width: Math.max(...xs) - Math.min(...xs) + 2 * side_padding,
      height: Math.max(...ys) - Math.min(...ys) + top_padding + bottom_padding,
    };
  }

  /**
   * Draw a glowing neon-green box around the patient (remote patient selection).
   * @param rect The box from patientHighlightRect (smoothed by the caller).
   */
  drawPatientHighlight({ x, y, width, height }) {
    this.ctx.save();
    this.ctx.strokeStyle = "#39FF14";
    this.ctx.shadowColor = "#39FF14";
    this.ctx.lineWidth = 6;
    this.ctx.lineJoin = "round";
    // Two passes: a wide soft glow, then a sharper one on top
    this.ctx.shadowBlur = 30;
    this.ctx.strokeRect(x, y, width, height);
    this.ctx.shadowBlur = 12;
    this.ctx.strokeRect(x, y, width, height);
    this.ctx.restore();
  }

  drawKeypoints3D(keypoints) {
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;
    const pointsData = keypoints.map((keypoint) => [
      -keypoint.x,
      -keypoint.y,
      -keypoint.z,
    ]);

    const dataset = new scatter.ScatterGL.Dataset([
      ...pointsData,
      ...ANCHOR_POINTS,
    ]);

    const keypointInd = posedetection.util.getKeypointIndexBySide(
      params.STATE.model
    );
    this.scatterGL.setPointColorer((i) => {
      if (keypoints[i] == null || keypoints[i].score < scoreThreshold) {
        // hide anchor points and low-confident points.
        return "#ffffff";
      }
      if (i === 0) {
        return "#ff0000" /* Red */;
      }
      if (keypointInd.left.indexOf(i) > -1) {
        return "#00ff00" /* Green */;
      }
      if (keypointInd.right.indexOf(i) > -1) {
        return "#ffa500" /* Orange */;
      }
    });

    if (!this.scatterGLHasInitialized) {
      this.scatterGL.render(dataset);
    } else {
      this.scatterGL.updateDataset(dataset);
    }
    const connections = posedetection.util.getAdjacentPairs(params.STATE.model);
    const sequences = connections.map((pair) => ({ indices: pair }));
    this.scatterGL.setSequences(sequences);
    this.scatterGLHasInitialized = true;
  }
}
